/** The welcome's behavior half: the client's name and their bookings from the dashboard read. Draws nothing. */
import { useMemo } from 'react';
import { webServicePath } from '@telify/shared/browser';
import { resolveBookTab } from '@/utils/bookTarget';

import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useWebDashboard } from '../../hooks/useWebAccount';
import { usePageMover } from '../../pageMover';
import { contract, type HomeWelcomeActions, type HomeWelcomeEvents, type HomeWelcomeState } from './contract';

export type HomeWelcomeRuntime = PieceRuntime<typeof contract, HomeWelcomeState, HomeWelcomeActions>;

const DAY = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export function useHomeWelcome(input: PieceInput<HomeWelcomeEvents> = {}): HomeWelcomeRuntime {
  const options = usePieceOptions(contract, input.options);
  const navigate = usePageMover();
  const d = useWebDashboard().data;
  const last = d?.recentHistory[0] ?? null;
  const lastSession = useMemo(
    () =>
      last
        ? `Your last session was ${last.serviceName}${last.providerName ? ` with ${last.providerName}` : ''} on ${DAY.format(new Date(last.completedAt))}.`
        : null,
    [last],
  );
  return {
    state: {
      status: d ? 'ready' : 'loading',
      firstName: d?.user.firstName ?? '',
      hasNext: Boolean(d?.nextBooking),
      lastSession,
      // Book opens the tab the studio has something on (resolveBookTab, Decision C).
      bookHref: d && resolveBookTab(d.availability) === 'events' ? '/events' : '/services',
      // An event's service has no page of its own on the website, so its Book again opens Events.
      bookAgain: last
        ? { label: `Book ${last.serviceName} again`, href: last.kind === 'event' ? '/events' : webServicePath(last.serviceId, last.serviceName) }
        : null,
    },
    actions: useMemo<HomeWelcomeActions>(() => ({ open: (href) => navigate(href) }), [navigate]),
    options,
  };
}
